import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  fetchShortlist,
  shortlistAddPlayer,
} from '../../../../redux/actions/Shortlist';
import {
  transferGetDataToSearchable,
  transferSetUserWithOfferToBuy,
  transferShowUnshowFilter,
} from '../../../../redux/actions/Transfers';
import { teamAddToList } from '../../../../redux/actions/Team';
import { sidebarOpenRightPanel } from '../../../../redux/actions/Sidebar';
import {
  getShortlistItems,
  getShortlistLoading,
} from '../../../../redux/selector/Shortlist';
import {
  getSearchPlayer,
  getSearchPlayerLoading,
} from '../../../../redux/selector/Transfers';
import { isPremiumUser } from '../../../../redux/selector/User';
import { ratingRangeStatus } from '../../../../service/constants';
import {
  formRangeStatus,
  simplifiedPosition,
} from '../../../../service/data';
import { clubLogoForTeam } from '../../../../service/hubClubLogos';
import ShortlistToggle from '../../../../components/ShortlistToggle';
import TeamOwnerIcon from '../TeamOwnerIcon';
import { ReactComponent as Crown } from '../../../../assets/img/crown.svg';
import {
  StyledPage,
  StyledHeaderRow,
  StyledUsage,
  StyledList,
  StyledRow,
  StyledCrest,
  StyledIdentity,
  StyledPlayerLink,
  StyledClub,
  StyledMetrics,
  StyledMetric,
  StyledNote,
  StyledNoteInput,
  StyledEmpty,
} from './styled';

const NOTE_MAX = 140;
const playerName = (player) => {
  if (!player) return 'Unknown player';
  const first = player.first_name || '';
  const last = player.last_name || '';
  return `${first} ${last}`.trim() || 'Unknown player';
};

export default function ShortlistPage() {
  const dispatch = useDispatch();
  const items = useSelector(getShortlistItems);
  const loading = useSelector(getShortlistLoading);
  const search = useSelector(getSearchPlayer);
  const searchLoading = useSelector(getSearchPlayerLoading);
  const isPremium = useSelector(isPremiumUser);
  const [draftNotes, setDraftNotes] = useState({});

  useEffect(() => {
    dispatch(fetchShortlist());
    dispatch(transferGetDataToSearchable());
  }, [dispatch]);

  const playersById = useMemo(() => {
    const map = {};
    const players = Array.isArray(search) ? search : search?.data || [];
    players.forEach((player) => {
      if (player?._id) map[String(player._id)] = player;
    });
    return map;
  }, [search]);

  const rows = useMemo(
    () =>
      items
        .map((item) => ({
          ...item,
          playerData: item.playerData || playersById[item.player],
        })),
    [items, playersById]
  );

  const limit = isPremium ? 100 : 10;

  const saveNote = (playerId) => {
    const note = draftNotes[playerId];
    if (note === undefined) return;
    dispatch(shortlistAddPlayer(playerId, note.slice(0, NOTE_MAX)));
  };

  const openPlayerDetails = (player) => {
    if (!player?._id) return;
    const forSidebar = {
      ...player,
      teamName:
        player.teamName ||
        player.team?.name ||
        (!player.team ? 'Free agent' : undefined),
    };
    dispatch(teamAddToList(forSidebar));
    dispatch(transferSetUserWithOfferToBuy(forSidebar));
    dispatch(transferShowUnshowFilter(false));
    dispatch(sidebarOpenRightPanel(true));
  };

  const onCardKeyDown = (event, player) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openPlayerDetails(player);
    }
  };

  return (
    <StyledPage>
      <StyledHeaderRow>
        <h2>Shortlist</h2>
        <StyledUsage>
          {items.length} / {limit}
          {!isPremium ? (
            <> · Free <span title="Patrons can save up to 100 players"><Crown /> Patron: 100</span></>
          ) : (
            <span><Crown /> Patron</span>
          )}
        </StyledUsage>
      </StyledHeaderRow>
      {(loading || (searchLoading && items.length > 0)) && rows.length === 0 ? (
        <StyledEmpty>Loading…</StyledEmpty>
      ) : rows.length === 0 ? (
        <StyledEmpty>
          No shortlisted players yet. Star players from{' '}
          <Link to="/transfers/search-players">Search players</Link>.
        </StyledEmpty>
      ) : (
        <StyledList>
          {rows.map((row) => {
            const player = row.playerData;
            const name = playerName(player);
            const noteValue =
              draftNotes[row.player] !== undefined
                ? draftNotes[row.player]
                : row.note || '';
            const position =
              simplifiedPosition[player?.fav_position] ||
              player?.fav_position ||
              '—';
            const clubName = player?.team?.name || 'Free agent';
            const isFreeAgent = !player?.team || clubName === 'Free agent';
            const crest =
              player?.team?.symbol || clubLogoForTeam(player?.team?.name);
            const metrics = [
              ['Position', position],
              ['Age', player?.age ?? '—'],
              ['Rating', ratingRangeStatus[player?.rating_range] || '—'],
              [
                'Potential',
                ratingRangeStatus[player?.potential_range] || '—',
              ],
              ['Fitness', formRangeStatus[player?.form_range] || '—'],
            ];
            return (
              <StyledRow
                key={row.player}
                role="button"
                tabIndex={0}
                aria-label={`Open details for ${name}`}
                onClick={() => openPlayerDetails(player)}
                onKeyDown={(event) => onCardKeyDown(event, player)}
              >
                <StyledCrest>
                  {crest ? (
                    <img src={crest} alt={`${clubName} crest`} />
                  ) : isFreeAgent ? (
                    <div className="free-agent-icon">
                      <TeamOwnerIcon
                        kind="freeAgent"
                        tooltipId={`shortlist-free-agent-${row.player}`}
                      />
                    </div>
                  ) : (
                    <span className="crest-placeholder" aria-hidden="true" />
                  )}
                </StyledCrest>
                <StyledIdentity>
                  <StyledPlayerLink as="span">{name}</StyledPlayerLink>
                  <StyledClub>{clubName}</StyledClub>
                </StyledIdentity>
                <StyledMetrics>
                  {metrics.map(([label, value]) => (
                    <StyledMetric key={label}>
                      <strong>{value}</strong>
                      <span>{label}</span>
                    </StyledMetric>
                  ))}
                </StyledMetrics>
                <div
                  className="star"
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  <ShortlistToggle playerId={row.player} size={20} />
                </div>
                <StyledNote
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  <StyledNoteInput
                    value={noteValue}
                    maxLength={NOTE_MAX}
                    placeholder="Add a note…"
                    aria-label={`Note for ${name}`}
                    onChange={(event) =>
                      setDraftNotes((prev) => ({
                        ...prev,
                        [row.player]: event.target.value,
                      }))
                    }
                    onBlur={() => saveNote(row.player)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') event.currentTarget.blur();
                    }}
                  />
                </StyledNote>
              </StyledRow>
            );
          })}
        </StyledList>
      )}
    </StyledPage>
  );
}
